// The underrun counter (P0.12, rebuilt at P4.14) — Architecture §12: "The engine maintains an
// underrun counter and watchdog."
//
// ## What an underrun is, and why this is not the obvious code
//
// An underrun is the audio thread failing to produce a block before the device needed it. The user
// hears a click. NFR-P2 budgets that at "≤1 audible glitch per 30-minute set (p95)", which is the
// tightest number in the PRD.
//
// The obvious implementation is to count skipped blocks on the audio thread: `currentFrame`
// advances by 128 per render quantum, so a gap means blocks were missed. **That check can never
// fire.** Measured at P0.12 against a worklet deliberately burning 8 ms inside a 2.9 ms block —
// roughly a 3x overload, audibly destroyed — `currentFrame` advanced by exactly 128 every single
// call, 0 skips. Chrome does not skip quanta when it is late; it renders every one of them, just
// too slowly. The audio device gets starved while the graph's own clock stays perfectly tidy.
//
// So the block counter is a green light wired to nothing. Two other roads were checked and closed
// at the same time:
//
//   * `AudioContext.renderCapacity` (Chromium's own `underrunRatio`) — **not implemented** in the
//     Chrome 148 this was measured on. Worth re-testing later; it would be strictly better.
//   * `performance.now()` inside the worklet — **`performance` is undefined** in
//     AudioWorkletGlobalScope. Only `Date.now()` (1 ms, far too coarse for a 2.9 ms block) exists.
//
// ## What actually fires
//
// The audio clock falls behind the wall clock. `context.currentTime` counts rendered audio; when
// the thread cannot keep up, real time runs away from it, and the gap is exactly the audio that
// should have been produced and was not. Measured at P0.12:
//
//   | healthy     | drift wanders in a ±6 ms band and does not trend (4 s run, range 10.11 ms)
//   | overloaded  | drift grew ~885 ms in 1.5 s, monotonically — ~590 ms/s
//
// ## ⚠️ Why the first version of this was wrong, and it took a 30-minute run to show it
//
// P0.12 counted `(maxDriftMs − 12 ms) / blockMs`: **cumulative** drift against a **fixed** floor.
// That is an absolute threshold on a quantity with no upper bound, so given enough time *any*
// steady divergence between the two clocks crosses it on a perfectly healthy machine. Time to the
// first false positive is `floor ÷ rate`, and nothing in the check knows how long the run was.
//
// Two 30-minute soaks on 11 September 2026, same machine, same commit, same DSP load, settled it:
//
//   | device at 44.1 kHz | drift rate **+0.3995 ms/s** (+400 ppm) | reported **247 late blocks** |
//   | device at 48 kHz   | drift rate **−0.0003 ms/s** (−0.3 ppm) | reported **0 late blocks**   |
//
// 1,336x apart with nothing about the audio thread's workload changed. The 44.1 kHz run was a
// context being resampled onto a 48 kHz device, and its drift accumulated **while both decks were
// stopped** — an idle audio thread cannot be starving. The detector was measuring the *device's
// crystal*, and P0.12's 4-second validation run was 7.5x too short for the failure mode to exist.
//
// ## What it counts now: the slope is physics, the steps are the fault
//
//   drift(t) = ρ·t            device clock offset — a constant slope, and not a fault
//            + Σ steps        blocks the engine owed and delivered late — NFR-P2's subject
//            + noise          ±6 ms quantisation sawtooth, bounded (see UNDERRUN_FLOOR_MS)
//
// The old check added all three together. This one separates them: the run's own baseline rate ρ̂ is
// fitted across every sample and subtracted, drift is differenced over short windows, and what
// remains above the noise floor is counted and summed. Summed, because NFR-P2's unit is glitches
// *per 30-minute set* rather than a worst moment.
//
// ## ⚠️ Why ρ̂ is a least-squares fit and not the obvious median
//
// A median of per-window rates is the robust-statistics reflex, and it was written first. It is
// wrong here, because **the noise is a sawtooth rather than a scatter** — the band is serially
// correlated by construction ("the device pulls in bursts of several quanta"). A sawtooth beating
// against a fixed window length biases every window the same way, and a median cannot average out
// a bias that is present in most of its inputs. Measured against a 400 ppm slope, 30 minutes:
//
//   | noise shape            | median of 2 s windows | least squares |
//   | iid draws from the band|            400 ppm ✓  |     400 ppm ✓ |
//   | sawtooth, 1333 ms      |            402 ppm ✓  |     400 ppm ✓ |
//   | sawtooth, 700 ms       |         **-314 ppm**  |     400 ppm ✓ |
//
// The median does not merely lose precision on the third row, it changes sign — and a baseline of
// the wrong sign is *added* to every window instead of removed, which manufactures the underruns
// this file exists to stop manufacturing. Least squares is exact on all three, because a slope plus
// bounded zero-mean noise is precisely the model it assumes.
//
// What the median was buying was robustness to a few bad windows, and that job now belongs entirely
// to `MAX_CLOCK_OFFSET_MS_PER_SECOND` — see there, because a detector that calibrates its own
// overload away would be the same bug again. Measured, same 30-minute run:
//
//   | one 200 ms stall     | fit reads  548 ppm — over-subtracts 0.3 ms/window, floor absorbs it |
//   | ten 1-second stalls  | fit reads 6766 ppm — **past the cap**, clamped, and counted          |
//
// ## Why this is main-thread code
//
// Because it can be. Both clocks are readable from here — `performance.now()` and
// `context.currentTime` — so the counter costs the audio thread *nothing*: no branch, no store, no
// `Date.now()` on a 2.9 ms deadline. Principle 1 is "audio is sacred", and the cheapest way to
// honour it is to not put the instrument inside the patient.
//
// A stalled main thread does not corrupt the reading, which is the property that makes this legal:
// both clocks are sampled at the same instant, and drift is a difference of *elapsed* times. If
// this thread is blocked for 200 ms, both clocks advance 200 ms and the drift is unchanged. That
// matters, because the bench deliberately stalls the main thread (R-1) while this is running.
//
// The class is pure — it is handed clock readings rather than reading them — so a 30-minute run at
// 400 ppm replays in a Node test in microseconds, which is the only reason the bug above could be
// turned into a regression test at all.

/**
 * The noise floor, measured rather than assumed (P0.12), and **now applied to a bounded quantity**.
 *
 * A healthy audio thread does not hold drift at zero. It sawtooths: the device pulls in bursts of
 * several quanta, and both clocks are quantised. Measured over a 4-second healthy run the band was
 * 10.11 ms peak-to-peak, and over a main-thread-sampled run, 10.56 ms. 12 ms sits just above both.
 *
 * ⚠️ **The number did not change at P4.14; what it is compared against did.** It used to sit
 * against cumulative drift, which grows without bound — so the floor was crossed eventually on any
 * machine. It now sits against the *change* in drift across one `DRIFT_WINDOW_MS` window, which the
 * band bounds no matter how long the run is.
 *
 * **That the band really is bounded, rather than merely small, is the load-bearing claim** — and it
 * is measured rather than assumed. A 4-second run draws ~40 samples from it; the 30-minute soak of
 * 11 September drew **18,000** and reached a maximum of **9.29 ms**, still under a floor derived
 * from the short run. A heavy-tailed distribution would have sailed past it with 450x the draws.
 * So sampling longer does not walk this threshold upward, which is exactly what went wrong before.
 *
 * The cost is a blind spot: a glitch smaller than 12 ms inside one window is not counted, so
 * `lateBlocks` is a **lower bound**. At ~4 blocks that is under anything audible as a dropout, and
 * the failure being guarded against is a bench reporting underruns on a machine that is fine —
 * which is how a budget gets ignored.
 */
export const UNDERRUN_FLOOR_MS = 12;

/**
 * How much drift is differenced at a time.
 *
 * The window is the whole trick, so the trade is worth stating. **Noise does not grow with the
 * window** — it is bounded by the band above however long you wait — while both a clock offset and
 * real starvation accumulate linearly inside it. So a longer window separates signal from noise
 * better, and the only thing pulling the other way is that a run has to contain enough windows for
 * a median to mean anything (`MIN_DRIFT_WINDOWS`).
 *
 * Two seconds is where those meet. At 100 ms sampling it is 20 readings a window; a 15-second bench
 * run gets 7 windows, the 60-second overload check that proves this counter works gets 30, and a
 * 30-minute soak gets 900. The worst real clock offset this project has measured (400 ppm)
 * contributes 0.8 ms across one window — comfortably inside the floor even before ρ̂ removes it.
 */
export const DRIFT_WINDOW_MS = 2000;
/**
 * How often the clocks should be read, in ms.
 *
 * Lives here rather than in a caller because there are two of them now — `/bench` and the app
 * itself (P4.16) — and the whole point of comparing their numbers is that they were sampled the
 * same way. Two copies of 100 is exactly the shape of bug this repo keeps paying for.
 *
 * 10 Hz gives twenty readings inside a `DRIFT_WINDOW_MS` window, which is plenty to place the
 * window's endpoints, and costs the main thread two clock reads and a subtraction — a poll nobody
 * will notice against a 16.7 ms frame budget. It is also far below the rate at which either clock
 * changes meaningfully: `currentTime` advances per render quantum, ~2.7 ms.
 */
export const DRIFT_SAMPLE_MS = 100;

/**
 * How many windows a run needs before this reports anything at all.
 *
 * Below it the report says `evaluated: false` rather than `lateBlocks: 0`. "Nothing was measured"
 * and "everything measured zero" are different facts, and a counter that returns the same value for
 * both is how `0 <= limit` passes vacuously — this repo's oldest bug, met at P0.1, P0.5, P0.8,
 * P0.10, P0.11 and P2.12.
 *
 * ⚠️ **Five windows is the least that can be said at all, not a length at which health is
 * certifiable.** The baseline fit's own error is roughly the noise band divided by the run's
 * length: ~1 ms/s over ten seconds, which would eat two of the floor's twelve. A short run can
 * still catch a gross overload — 590 ms/s needs no precision, and the cap handles it — but it
 * should never be read as a clean bill of health. What makes that acceptable rather than a hole is
 * that `nfr-p2-underruns` refuses anything under thirty minutes for its own reasons, and thirty
 * minutes puts the fit's error three orders of magnitude under the floor.
 */
export const MIN_DRIFT_WINDOWS = 5;

/**
 * The fastest the two clocks may diverge and still be blamed on hardware, in ms per second.
 *
 * ⚠️ **This constant is the loophole guard, and without it the rest of the file is unsound.** The
 * baseline rate is fitted from the run's own samples, so a machine that was overloaded for the
 * *whole* run would measure its own overload, subtract it as "baseline", and report a clean zero.
 * That is a detector that cannot fail — worse than none, because it looks like coverage. It is also
 * the only guard left, now that ρ̂ is a least-squares fit rather than a median: the fit has no
 * robustness of its own and is not meant to.
 *
 * So the estimate is capped at what a crystal can actually do. 1 ms/s is 1000 ppm: 0.1%, or 86
 * seconds a day, which is already a badly out-of-spec part. For scale, in this project's own
 * measurements:
 *
 *   | a 48 kHz device, native          |      0.3 ppm |    3000x inside the cap |
 *   | a 44.1 kHz context, resampled    |    400   ppm |     2.5x inside the cap |
 *   | P0.12's deliberate 3x overload   | 590000   ppm |      590x past the cap  |
 *
 * The gap between the worst real device and the mildest real overload is three orders of magnitude,
 * which is what makes a cap here honest rather than a guess. A run that exceeds it is reported with
 * `baselineClamped`, because "the clocks diverged faster than any crystal explains" is itself a
 * finding and should never be silently absorbed.
 */
export const MAX_CLOCK_OFFSET_MS_PER_SECOND = 1;

/** Parts per million, from ms per second. A rate of 1 ms/s is one part in a thousand. */
const PPM_PER_MS_PER_SECOND = 1000;

export interface ClockSample {
  /** `performance.now()`, in ms. Monotonic; unaffected by the system clock moving. */
  readonly wallMs: number;
  /** `context.currentTime * 1000`, in ms. Counts audio actually rendered. */
  readonly audioMs: number;
}

export interface UnderrunReport {
  /**
   * How far behind real time the audio thread ended up, in ms. **A diagnostic, not the verdict.**
   *
   * Can be negative, and that is not a bug — it is the noise band, and on a device whose crystal
   * runs fast it is where a whole healthy run sits. Reported raw rather than clamped so a reader
   * can see the instrument's own scatter instead of a floor of zeroes implying a precision this
   * does not have.
   */
  readonly driftMs: number;
  /**
   * The worst drift seen during the run, not merely where it finished.
   *
   * ⚠️ **No longer what `lateBlocks` is computed from**, and the distinction is the P4.14 fix. On a
   * 30-minute run against a 400 ppm device this reads ~730 ms while the engine is perfectly
   * healthy. It is kept because it is a real observation about the run; it is no longer evidence
   * about the engine.
   */
  readonly maxDriftMs: number;
  /**
   * Drift growth per second of wall clock, over the whole run. Also a diagnostic: it is dominated
   * by the device's clock offset, which is what `baselineRateMsPerSecond` now measures deliberately.
   */
  readonly driftRateMsPerSecond: number;
  /**
   * The run's own steady divergence, in ms per second — what was subtracted before counting.
   *
   * The median of the per-window rates, capped at `MAX_CLOCK_OFFSET_MS_PER_SECOND`. Reported so the
   * subtraction is visible: a reader can check that what was removed is a plausible crystal rather
   * than an engine fault being explained away.
   */
  readonly baselineRateMsPerSecond: number;
  /**
   * The same divergence in parts per million — a property of the *machine*, not of the run.
   *
   * The unit is the point. 400 ppm is a number that can be compared against a device's datasheet,
   * against the same machine next month, and against another machine; "0.3995 ms/s" is a number
   * that gets read once and forgotten. Uncapped, unlike `baselineRateMsPerSecond`, because the
   * honest measurement is what belongs here even when it is too large to be a crystal.
   *
   * Null when the run was too short to measure one.
   */
  readonly clockOffsetPpm: number | null;
  /**
   * ⚠️ True when the measured baseline exceeded what a crystal can explain and was capped.
   *
   * Not a detail. It means the clocks diverged steadily at a rate no hardware accounts for, which
   * is a sustained overload rather than a device — and it is the case where `lateBlocks` below is a
   * floor rather than a count, because a capped baseline still subtracts 1 ms/s that was probably
   * the engine's fault.
   */
  readonly baselineClamped: boolean;
  /**
   * Blocks of audio the engine owed the device and did not deliver on time — window excess past the
   * measured noise floor, in block periods, summed across the run. This is the count NFR-P2 is
   * about, and it is a **lower bound**: see `UNDERRUN_FLOOR_MS` for the blind spot it buys.
   *
   * Zero on a healthy machine by construction, whatever the device's crystal is doing and however
   * long the run lasts — which is what P0.12's version could not promise.
   */
  readonly lateBlocks: number;
  /** True once any window has exceeded the floor. What a watchdog (§12) would act on. */
  readonly underran: boolean;
  /**
   * ⚠️ How close the worst window came to the floor, in ms — **negative is margin**.
   *
   * This exists because `lateBlocks: 0` is exactly as uninformative as it has always been: it says
   * nothing about whether the run cleared the floor by 8 ms or by 0.2 ms. The old detector's floor
   * was calibrated against a **4-second** measurement of the noise band (10.11 ms peak-to-peak) and
   * is now being asked to bound the change in drift across a 2-second window, which is a related
   * but *not identical* quantity. Nobody has measured the second one.
   *
   * So rather than assert the floor is comfortable, the report shows the margin and lets a real
   * soak answer. A run reading −8 ms here has a floor with room in it; a run reading −0.3 ms is one
   * warm afternoon away from manufacturing underruns, and that should be visible **before** it
   * starts happening rather than inferred afterwards from a count that quietly left zero.
   *
   * Null when no window was closed.
   */
  readonly worstWindowExcessMs: number | null;
  /** Windows closed. Below `MIN_DRIFT_WINDOWS` there is no verdict — see `evaluated`. */
  readonly windowCount: number;
  /**
   * Whether `lateBlocks` is a measurement at all.
   *
   * False means the run was too short to hold `MIN_DRIFT_WINDOWS`, and `lateBlocks` is 0 because
   * nothing was counted rather than because nothing was wrong. Every caller that compares
   * `lateBlocks` to a budget must read this first.
   */
  readonly evaluated: boolean;
  /** Clock samples taken. Zero means nothing was measured — not that nothing was wrong. */
  readonly sampleCount: number;
  /** The threshold applied, so the figures above are readable without reading this file. */
  readonly floorMs: number;
  /** The block period these `lateBlocks` are counted in — device sample rate decides it. */
  readonly blockMs: number;
}

/** One closed window: how much drift accrued, and over how much wall clock. */
interface DriftWindow {
  readonly driftDeltaMs: number;
  readonly wallDeltaMs: number;
}

/**
 * A least-squares slope, accumulated one sample at a time.
 *
 * Welford's form rather than the textbook sums of squares, and not as a flourish: `Σt²` over a
 * 30-minute run in milliseconds reaches ~6e16, past the 9e15 where a float64 stops representing
 * integers exactly, so the naive form loses precision on exactly the long runs this exists for.
 * Tracking the running means instead keeps every quantity near zero. Time is carried in **seconds**
 * for the same reason, which also makes the slope come out in ms per second with no conversion.
 */
class SlopeFit {
  #count = 0;
  #meanSeconds = 0;
  #meanDrift = 0;
  /** Σ(t − t̄)², the spread of the time axis. Zero until two distinct times have been seen. */
  #timeVariance = 0;
  /** Σ(t − t̄)(d − d̄). */
  #covariance = 0;

  add(seconds: number, driftMs: number): void {
    this.#count += 1;
    const timeResidual = seconds - this.#meanSeconds;
    const driftResidual = driftMs - this.#meanDrift;
    this.#meanSeconds += timeResidual / this.#count;
    this.#meanDrift += driftResidual / this.#count;
    this.#timeVariance += timeResidual * (seconds - this.#meanSeconds);
    this.#covariance += timeResidual * (driftMs - this.#meanDrift);
  }

  reset(): void {
    this.#count = 0;
    this.#meanSeconds = 0;
    this.#meanDrift = 0;
    this.#timeVariance = 0;
    this.#covariance = 0;
  }

  /** Drift growth per second. Zero when every sample landed at the same instant. */
  get slopeMsPerSecond(): number {
    return this.#timeVariance > 0 ? this.#covariance / this.#timeVariance : 0;
  }
}

/**
 * Watches the audio clock against the wall clock.
 *
 * Fed by whoever owns the clocks (the engine, from a poll). Holds no clock of its own, which is
 * what makes a 900 ms stall — or a 30-minute run against a 400 ppm crystal — reproducible in a unit
 * test in 0 ms.
 */
export class ClockDriftMonitor {
  readonly #blockMs: number;
  #base: ClockSample | null = null;
  #latest: ClockSample | null = null;
  #maxDriftMs = 0;
  #sampleCount = 0;
  /** Where the window currently being filled began. */
  #windowStart: ClockSample | null = null;
  readonly #windows: DriftWindow[] = [];
  readonly #fit = new SlopeFit();

  /**
   * @param sampleRateHz The context's real rate — read from the context, never assumed. P0.5 came
   *   up at 44.1 kHz where a 128-frame quantum is 2.9 ms, not the 2.7 ms every document says.
   * @param blockFrames The render quantum. 128 everywhere today, and a spec constant rather than a
   *   preference — but named, because reading `128` as a magic number three files later is how
   *   `renderQuantumSize` becoming configurable turns into a silent unit error.
   */
  constructor(sampleRateHz: number, blockFrames = 128) {
    this.#blockMs = (blockFrames / sampleRateHz) * 1000;
  }

  /**
   * Take a reading.
   *
   * The first sample is the baseline and contributes no drift — drift is a *change* in the
   * relationship between two clocks, and their absolute offset is meaningless (one starts at page
   * load, the other when the context did).
   */
  sample(sample: ClockSample): void {
    this.#sampleCount += 1;

    if (this.#base === null) {
      this.#base = sample;
      this.#latest = sample;
      this.#windowStart = sample;
      return;
    }

    this.#latest = sample;
    const drift = this.#driftAt(sample);
    if (drift > this.#maxDriftMs) {
      this.#maxDriftMs = drift;
    }
    this.#fit.add((sample.wallMs - this.#base.wallMs) / 1000, drift);

    // Close the window once it has covered its span. Measured against wall time actually elapsed
    // rather than a count of samples, because the poll driving this is a `setInterval` on a thread
    // the bench deliberately stalls — counting ticks would make the window's length a fiction
    // exactly when the run is most interesting.
    const start = this.#windowStart;
    if (start !== null && sample.wallMs - start.wallMs >= DRIFT_WINDOW_MS) {
      this.#windows.push({
        driftDeltaMs: this.#driftAt(sample) - this.#driftAt(start),
        wallDeltaMs: sample.wallMs - start.wallMs,
      });
      this.#windowStart = sample;
    }
  }

  /** Drop the baseline, so a fresh run is measured on its own. */
  reset(): void {
    this.#base = null;
    this.#latest = null;
    this.#maxDriftMs = 0;
    this.#sampleCount = 0;
    this.#windowStart = null;
    this.#windows.length = 0;
    this.#fit.reset();
  }

  /**
   * ⚠️ **O(the windows so far), so do not call this on a frame budget.** It walks every closed
   * window to sum the late blocks and find the worst, and a run accumulates one window every
   * `DRIFT_WINDOW_MS` — 1,800 in an hour. `sample()` is the O(1) half; this is the half to call
   * when something actually needs the answer. The app reads it at 2 Hz while sampling at 10.
   */
  get report(): UnderrunReport {
    const base = this.#base;
    const latest = this.#latest;

    if (base === null || latest === null) {
      return {
        driftMs: 0,
        maxDriftMs: 0,
        driftRateMsPerSecond: 0,
        baselineRateMsPerSecond: 0,
        clockOffsetPpm: null,
        baselineClamped: false,
        lateBlocks: 0,
        underran: false,
        worstWindowExcessMs: null,
        windowCount: 0,
        evaluated: false,
        sampleCount: this.#sampleCount,
        floorMs: UNDERRUN_FLOOR_MS,
        blockMs: this.#blockMs,
      };
    }

    const driftMs = this.#driftAt(latest);
    const elapsedMs = latest.wallMs - base.wallMs;
    const windows = this.#windows;
    const evaluated = windows.length >= MIN_DRIFT_WINDOWS;

    // The run's own steady divergence, fitted across every sample rather than estimated from the
    // windows. See the header for the measurement that rejected the median this replaced: against a
    // sawtooth band it returned the wrong *sign*, and a baseline of the wrong sign is added to each
    // window instead of removed.
    const measuredRate = this.#fit.slopeMsPerSecond;
    const baselineRateMsPerSecond = Math.max(
      -MAX_CLOCK_OFFSET_MS_PER_SECOND,
      Math.min(MAX_CLOCK_OFFSET_MS_PER_SECOND, measuredRate),
    );

    // Per window: what the clocks did, minus what the device's crystal explains, minus the noise
    // band. Summed, because NFR-P2 counts glitches across a set rather than the worst one.
    //
    // The worst window is tracked whether or not the run is long enough to be judged: a short run
    // cannot produce a verdict, but it can still say how close the floor came to firing.
    let lateBlocks = 0;
    let worstWindowExcessMs: number | null = null;
    for (const window of windows) {
      const expectedMs = (baselineRateMsPerSecond * window.wallDeltaMs) / 1000;
      const excessMs = window.driftDeltaMs - expectedMs - UNDERRUN_FLOOR_MS;
      if (worstWindowExcessMs === null || excessMs > worstWindowExcessMs) {
        worstWindowExcessMs = excessMs;
      }
      if (evaluated && excessMs > 0) {
        lateBlocks += Math.floor(excessMs / this.#blockMs);
      }
    }

    return {
      driftMs,
      maxDriftMs: this.#maxDriftMs,
      // Guarded: two samples in the same millisecond would divide by zero and report Infinity
      // ms/s, which would then fail a budget for a reason that has nothing to do with audio.
      driftRateMsPerSecond: elapsedMs > 0 ? (driftMs / elapsedMs) * 1000 : 0,
      baselineRateMsPerSecond,
      clockOffsetPpm: windows.length === 0 ? null : measuredRate * PPM_PER_MS_PER_SECOND,
      baselineClamped: Math.abs(measuredRate) > MAX_CLOCK_OFFSET_MS_PER_SECOND,
      lateBlocks,
      underran: lateBlocks > 0,
      worstWindowExcessMs,
      windowCount: windows.length,
      evaluated,
      sampleCount: this.#sampleCount,
      floorMs: UNDERRUN_FLOOR_MS,
      blockMs: this.#blockMs,
    };
  }

  /** Wall time elapsed minus audio time elapsed. Positive means the audio thread is behind. */
  #driftAt(sample: ClockSample): number {
    const base = this.#base;
    if (base === null) {
      return 0;
    }
    return sample.wallMs - base.wallMs - (sample.audioMs - base.audioMs);
  }
}
